import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFeatureFlagEnabled } from "posthog-js/react";
import {
  type ColumnSizingState,
  type ColumnVisibilityState,
  useTable,
} from "@tanstack/react-table";
import type { DocumentSummary } from "~/api/types";
import { CenterSpinner } from "~/atoms/spinner";
import { DataTable } from "~/molecules/DataTable";
import { dataTableFeatures } from "~/molecules/dataTableFeatures";
import NoResult from "~/molecules/NoResult";
import { usePersistentState } from "~/hooks/usePersistentState";
import {
  StickyHorizontalScrollbar,
  hiddenNativeScrollbarClass,
} from "~/molecules/StickyHorizontalScrollbar";
import { useUserContext } from "~/hooks/useUserContext";
import { featureFlags } from "~/config/featureFlags";
import { DocumentActiveFilterChips } from "~/organisms/filters/DocumentActiveFilterChips";
import { DocumentFiltersPanel } from "~/organisms/filters/DocumentFiltersPanel";
import { FilterableContentLayout } from "~/organisms/filters/FilterableContentLayout";
import { FilterToggleButton } from "~/molecules/FilterToggleButton";
import SearchBar from "~/molecules/SearchBar";
import { Toolbar } from "~/molecules/Toolbar";
import { FieldEditDialog } from "./FieldEditDialog";
import { DocumentsColumnsMenu } from "./DocumentsColumnsMenu";
import {
  createDocumentsColumns,
  getDocumentsListColumnSpecs,
  getEditableFieldByColumnId,
  resolveDocumentsColumnMenu,
} from "./columnRegistry";
import { isEditableField } from "~/helpers/libraryEditing";
import type { ActiveFieldEdit } from "~/helpers/libraryTypes";
import type { DocumentsListModel } from "~/hooks/useDocumentsListModel";

interface DocumentsListProps {
  model: DocumentsListModel;
}

export const DocumentsList = ({ model }: DocumentsListProps) => {
  const { t } = useTranslation();
  const isReferenceNumberEnabled =
    useFeatureFlagEnabled(featureFlags.referenceNumber) ?? false;
  const { user } = useUserContext();
  const [hiddenColumnIds, setHiddenColumnIds] = usePersistentState<string[]>(
    "library:documentsList:hiddenColumns",
    [],
  );
  const columnVisibility: ColumnVisibilityState = Object.fromEntries(
    hiddenColumnIds.map((id) => [id, false]),
  );
  const handleColumnVisibilityChange = (
    updater:
      | ColumnVisibilityState
      | ((old: ColumnVisibilityState) => ColumnVisibilityState),
  ) => {
    setHiddenColumnIds((prevHidden) => {
      const prevVisibility: ColumnVisibilityState = Object.fromEntries(
        prevHidden.map((id) => [id, false]),
      );
      const next =
        typeof updater === "function" ? updater(prevVisibility) : updater;
      return Object.entries(next).flatMap(([id, isVisible]) =>
        isVisible ? [] : [id],
      );
    });
  };
  const [columnSizing, setColumnSizing] = usePersistentState<ColumnSizingState>(
    "library:documentsList:columnSizing",
    {},
  );
  const [activeFieldEdit, setActiveFieldEdit] =
    useState<ActiveFieldEdit | null>(null);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  const tableWrapperRef = useRef<HTMLDivElement>(null);

  const filteredTagIds = model.filters.getInValues("tag");
  const columnSpecs = useMemo(
    () =>
      getDocumentsListColumnSpecs({
        referenceNumber: isReferenceNumberEnabled,
      }),
    [isReferenceNumberEnabled],
  );
  const columns = useMemo(
    () =>
      createDocumentsColumns({
        t,
        currentUserId: user.id,
        filteredTagIds,
        columnSpecs,
      }),
    [columnSpecs, t, user.id, filteredTagIds],
  );
  const editableFieldByColumnId = useMemo(
    () => getEditableFieldByColumnId(columnSpecs),
    [columnSpecs],
  );

  const table = useTable({
    features: dataTableFeatures,
    data: model.filteredDocuments,
    columns,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    state: {
      sorting: model.sorting,
      columnVisibility,
      columnSizing,
    },
    onSortingChange: model.handleSortingChange,
    onColumnVisibilityChange: handleColumnVisibilityChange,
    onColumnSizingChange: setColumnSizing,
    getRowId: (row) => row.id.toString(),
  });
  const getCellOnClick = useCallback(
    (columnId: string, rowData: DocumentSummary) => {
      const editableField = editableFieldByColumnId[columnId];
      const canEditField = isEditableField(rowData, editableField);

      if (!canEditField || !editableField) {
        return undefined;
      }

      return () => {
        setActiveFieldEdit({
          documentId: rowData.id,
          releaseId: rowData.latestRelease.id,
          field: editableField,
        });
      };
    },
    [editableFieldByColumnId],
  );

  if (model.isLoading) {
    return <CenterSpinner withText />;
  }

  if (!model.hasData) {
    return <NoResult message={t(($) => $.library.noDocumentsFound)} />;
  }

  const { columnMenuLabels, resolvedGroups, ungroupedColumns } =
    resolveDocumentsColumnMenu({
      columnSpecs,
      table,
      t,
    });

  return (
    <div className="space-y-4">
      <FieldEditDialog
        activeEdit={activeFieldEdit}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            setActiveFieldEdit(null);
          }
        }}
        onSaved={async () => {
          await model.mutate();
        }}
      />
      <Toolbar
        left={
          <>
            <FilterToggleButton
              isOpen={isFiltersOpen}
              onToggle={() => setIsFiltersOpen((current) => !current)}
            />
            <SearchBar
              placeholder={t(($) => $.library.filterByTitlePlaceholder)}
              value={model.titleSearch}
              onSearch={model.handleTitleSearchChange}
              onDebouncedSearch={model.handleTitleSearchChange}
              onClearSearch={() => model.handleTitleSearchChange("")}
            />
          </>
        }
        right={
          <DocumentsColumnsMenu
            ungroupedColumns={ungroupedColumns}
            resolvedGroups={resolvedGroups}
            columnMenuLabels={columnMenuLabels}
          />
        }
      />

      <FilterableContentLayout
        contentClassName="space-y-4"
        filters={<DocumentFiltersPanel filters={model.filters} />}
        isFiltersOpen={isFiltersOpen}
        sidebarLabel={t(($) => $.commons.filter.title)}
      >
        <DocumentActiveFilterChips filters={model.filters} />
        <DataTable
          table={table}
          noResultsLabel={t(($) => $.library.noResults)}
          getCellOnClick={getCellOnClick}
          wrapperRef={tableWrapperRef}
          wrapperClassName={hiddenNativeScrollbarClass}
          virtualizeRows
        />
      </FilterableContentLayout>
      <StickyHorizontalScrollbar
        targetRef={tableWrapperRef}
        deps={[
          model.filteredDocuments,
          columnVisibility,
          columnSizing,
          isFiltersOpen,
          columns,
        ]}
        className="fixed bottom-0 z-50 h-4 overflow-y-hidden"
      />
    </div>
  );
};
